import React, { useState, useRef, useEffect } from "react";
import {
  getMembersForRole,
  getUnassignedMembersForRole,
  getMemberDisplayName,
  isSmallLocation,
  filterMembersBySearch,
} from "./duck/helpers";

/**
 * Avatar component for a member row. Shows image or initials placeholder.
 */
function MemberAvatar({ member, size }) {
  const className = size === 'small' ? 'search-result-avatar' : 'member-avatar';
  const placeholderClass = size === 'small' ? 'search-result-avatar-placeholder' : 'member-avatar-placeholder';

  if (member.profile_image_thumb_url) {
    return (
      <img
        className={className}
        src={member.profile_image_thumb_url}
        alt={getMemberDisplayName(member)}
      />
    );
  }

  const initials = `${(member.first_name || '')[0] || ''}${(member.last_name || '')[0] || ''}`.toUpperCase() || '?';
  return (
    <div className={placeholderClass}>
      {initials}
    </div>
  );
}

/**
 * Right panel: shows members assigned to a role and allows add/remove.
 * Switches between small-location mode (full list) and large-location mode (search).
 */
export function RoleMembersPanel({
  selectedRoleId,
  members,
  membersLoading,
  assigningUserId,
  onAddRoleToUser,
  onRemoveRoleFromUser,
  roles,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Reset search when selected role changes
  useEffect(() => {
    setSearchQuery('');
    setShowDropdown(false);
  }, [selectedRoleId]);

  const assignedMembers = getMembersForRole(members, selectedRoleId);
  const unassignedMembers = getUnassignedMembersForRole(members, selectedRoleId);
  const smallLocation = isSmallLocation(members);

  // For large locations: filter unassigned by search
  const filteredUnassigned = filterMembersBySearch(unassignedMembers, searchQuery);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowDropdown(e.target.value.trim().length > 0);
  };

  const handleSearchFocus = () => {
    if (searchQuery.trim().length > 0) {
      setShowDropdown(true);
    }
  };

  const handleAddFromSearch = (member) => {
    onAddRoleToUser(member.owner_id, member.user?.tag_ids || []);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleAdd = (member) => {
    onAddRoleToUser(member.owner_id, member.user?.tag_ids || []);
  };

  const handleRemove = (member) => {
    onRemoveRoleFromUser(member.owner_id, member.user?.tag_ids || []);
  };

  // Find the role name for the panel title
  const selectedRole = (roles || []).find((r) => r.id === selectedRoleId);
  const roleName = selectedRole ? selectedRole.name : 'Role';

  if (membersLoading) {
    return (
      <div className="role-members-panel">
        <div className="panel-loading">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="role-members-panel">
      <div className="panel-title">{roleName}</div>
      <div className="panel-subtitle">
        {assignedMembers.length} member{assignedMembers.length !== 1 ? 's' : ''} assigned
      </div>

      {/* Large location: search box */}
      {!smallLocation && (
        <div className="role-member-search-wrap" ref={searchRef}>
          <input
            className="role-member-search-input"
            type="text"
            placeholder="Search to add members..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
          />
          {showDropdown && (
            <div className="role-member-search-dropdown" ref={dropdownRef}>
              {filteredUnassigned.length === 0 ? (
                <div className="search-no-results">No matching members</div>
              ) : (
                filteredUnassigned.slice(0, 10).map((member) => (
                  <div
                    key={member.id}
                    className="search-result-item"
                    onMouseDown={() => handleAddFromSearch(member)}
                  >
                    <MemberAvatar member={member} size="small" />
                    <span>+ {getMemberDisplayName(member)}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Scrollable member list */}
      <div className="panel-members-scroll">
        <label className="panel-section-label">Members</label>
        {assignedMembers.length === 0 ? (
          <div className="panel-empty">No members assigned to this role</div>
        ) : (
          assignedMembers.map((member) => (
            <div className="member-row" key={member.id}>
              <MemberAvatar member={member} />
              <div className="member-info">
                <div className="member-name">{getMemberDisplayName(member)}</div>
                {member.position && <div className="member-position">{member.position}</div>}
              </div>
              <button
                className="member-action-btn member-remove-btn"
                onClick={() => handleRemove(member)}
                disabled={assigningUserId === member.owner_id}
              >
                Remove
              </button>
            </div>
          ))
        )}

        {/* Small location: add members list */}
        {smallLocation && (
          <>
            <hr className="panel-divider" />
            <label className="panel-section-label">Add Members</label>
            {unassignedMembers.length === 0 ? (
              <div className="panel-empty">All members already have this role</div>
            ) : (
              unassignedMembers.map((member) => (
                <div className="member-row" key={member.id}>
                  <MemberAvatar member={member} />
                  <div className="member-info">
                    <div className="member-name">{getMemberDisplayName(member)}</div>
                    {member.position && <div className="member-position">{member.position}</div>}
                  </div>
                  <button
                    className="member-action-btn member-add-btn"
                    onClick={() => handleAdd(member)}
                    disabled={assigningUserId === member.owner_id}
                  >
                    + Add
                  </button>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RoleMembersPanel;
